import React from "react";
import { motion } from "framer-motion";

// ─────────────────────────────────────────────────────────
// Chunky filled section badges (used next to eyebrows)
// ─────────────────────────────────────────────────────────
export function SectionBadge({ label, num, color = "#FF4D00", icon }) {
  return (
    <div className="inline-flex items-center gap-3 select-none">
      <span
        className="relative inline-flex items-center justify-center w-9 h-9 border border-black text-black"
        style={{ background: color }}
      >
        {icon}
      </span>
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/80">
        {label} <span className="text-black/40 ml-1">// {num}</span>
      </span>
    </div>
  );
}

// ─── Filled badge symbols - chunky, geometric ──
export const IconSparkle = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 1l2.6 7.4L22 11l-7.4 2.6L12 21l-2.6-7.4L2 11l7.4-2.6L12 1z" />
  </svg>
);
export const IconEye = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
);
export const IconChart = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <rect x="3" y="12" width="4" height="9" />
    <rect x="10" y="7" width="4" height="14" />
    <rect x="17" y="3" width="4" height="18" />
  </svg>
);
export const IconStar = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2l3 6.5 7 1-5 5 1.2 7-6.2-3.5-6.2 3.5L7 14.5l-5-5 7-1L12 2z" />
  </svg>
);
export const IconHandshake = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M2 12l4-6h6l3 3-4 4-2-2-3 3 5 5h6l3-3-6-6 3-3H8l4 4-2 2-5-5H2v4z" />
  </svg>
);
export const IconMail = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);
export const IconQuote = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M6 6h5v5l-3 6H4l3-6H4V6h2zm9 0h5v5l-3 6h-4l3-6h-3V6h2z" />
  </svg>
);

// ─────────────────────────────────────────────────────────
// Highlight pill - replaces italic emphasis
// ─────────────────────────────────────────────────────────
export function Highlight({ children, color = "#FF4D00", textColor = "#fdfbf7", rotate = -1.5 }) {
  return (
    <span
      className="relative inline-block px-3 py-0.5 -mx-1 align-baseline"
      style={{
        background: color,
        color: textColor,
        transform: `rotate(${rotate}deg)`,
        boxShadow: "3px 3px 0 rgba(0,0,0,.9)",
      }}
    >
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────
// Masked line-by-line reveal (hero signature moment)
// ─────────────────────────────────────────────────────────
export function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function FadeUp({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
